import type { AnyAction, ThunkDispatch } from '@reduxjs/toolkit';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { OrganizerUser } from 'modules/roles-manager/domain/interfaces/organizer-user';
import roleUsers, {
  CreateUserPayload,
  DeleteUserPayload,
  GetUsersPayload,
  UpdateUserPayload,
} from 'modules/roles-manager/domain/resources/role-users';
import { restApi } from 'shared/api/rest-api';
import { toApiError } from 'shared/errors';

export interface GetRoleUsersArgs {
  /** Id организации (backend `tournamentId`). */
  organizerId: number;
  limit: number;
  offset: number;
  filters?: Omit<GetUsersPayload, 'limit' | 'offset'>;
}

export interface RoleUserMutationArgs<Payload> {
  organizerId: number;
  payload: Payload;
}

const pageTag = (organizerId: number, offset: number) => ({ type: 'RoleManager' as const, id: `${organizerId}:${offset}` });

interface RestApiRootState {
  [restApi.reducerPath]: ReturnType<typeof restApi.reducer>;
}

interface CachedPage {
  args: GetRoleUsersArgs;
  users: OrganizerUser[];
}

/** Закэшированные страницы организации — из состояния слайса (в RTK 1.9 нет `selectCachedArgsForQuery`). */
function cachedPages(state: RestApiRootState, organizerId: number): CachedPage[] {
  return Object.values(state[restApi.reducerPath].queries).flatMap((entry) => {
    if (!entry || entry.endpointName !== 'getRoleUsers' || entry.status !== QueryStatus.fulfilled) return [];
    const args = entry.originalArgs as GetRoleUsersArgs;
    if (args.organizerId !== organizerId) return [];
    return [{ args, users: entry.data as OrganizerUser[] }];
  });
}

/** Перезапросить страницы организации начиная с `fromOffset` — «хвост», куда сдвинулись пользователи. */
function invalidateTail(dispatch: ThunkDispatch<unknown, unknown, AnyAction>, pages: CachedPage[], fromOffset: number) {
  const tags = pages.filter(({ args }) => args.offset >= fromOffset).map(({ args }) => pageTag(args.organizerId, args.offset));
  if (tags.length > 0) dispatch(restApi.util.invalidateTags(tags));
}

const asApiError = (error: unknown) => ({ error: toApiError(error) });

/**
 * Пользователи организации (`roles-manager`), REST `tournaments/{id}/users`. Транспорт — `roleUsers`
 * (`domain/resources/role-users.ts`): он маппит DTO и после `PUT` грузит фото вторым запросом.
 *
 * Список постраничный: страница — отдельная запись кэша с тегом `RoleManager` / `<организация>:<offset>`.
 * Мутации обновляют кэш по [руководству RTK](https://redux-toolkit.js.org/rtk-query/usage/manual-cache-updates):
 * - правка заменяет пользователя на всех закэшированных страницах — без запросов;
 * - создание и удаление сдвигают пользователей между страницами, поэтому перезапрашивается только «хвост»:
 *   при создании — от последней страницы с данными, при удалении — от первой страницы, где был удалённый.
 */
export const roleUsersApi = restApi.injectEndpoints({
  endpoints: (build) => ({
    getRoleUsers: build.query<OrganizerUser[], GetRoleUsersArgs>({
      async queryFn({ organizerId, limit, offset, filters }) {
        try {
          return { data: await roleUsers.getUsers(organizerId, { limit, offset, ...(filters ?? {}) }) };
        } catch (error) {
          return asApiError(error);
        }
      },
      providesTags: (_result, _error, { organizerId, offset }) => [pageTag(organizerId, offset)],
    }),

    createRoleUser: build.mutation<OrganizerUser, RoleUserMutationArgs<CreateUserPayload>>({
      async queryFn({ organizerId, payload }) {
        try {
          return { data: await roleUsers.createUser(organizerId, payload) };
        } catch (error) {
          return asApiError(error);
        }
      },
      async onQueryStarted({ organizerId }, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        const pages = cachedPages(getState() as RestApiRootState, organizerId);
        const offsetsWithData = pages.filter(({ users }) => users.length > 0).map(({ args }) => args.offset);
        // Пустой список — перечитать всё: иначе первый созданный пользователь не появился бы.
        invalidateTail(dispatch, pages, offsetsWithData.length > 0 ? Math.max(...offsetsWithData) : 0);
      },
    }),

    updateRoleUser: build.mutation<OrganizerUser, RoleUserMutationArgs<UpdateUserPayload>>({
      async queryFn({ organizerId, payload }) {
        try {
          return { data: await roleUsers.updateUser(organizerId, payload) };
        } catch (error) {
          return asApiError(error);
        }
      },
      async onQueryStarted({ organizerId }, { dispatch, getState, queryFulfilled }) {
        let updated: OrganizerUser;
        try {
          ({ data: updated } = await queryFulfilled);
        } catch {
          return;
        }
        cachedPages(getState() as RestApiRootState, organizerId).forEach(({ args }) => {
          dispatch(
            roleUsersApi.util.updateQueryData('getRoleUsers', args, (draft) => {
              const index = draft.findIndex((user) => user.id === updated.id);
              if (index !== -1) draft[index] = updated;
            }),
          );
        });
      },
    }),

    deleteRoleUser: build.mutation<boolean, RoleUserMutationArgs<DeleteUserPayload>>({
      async queryFn({ organizerId, payload }) {
        try {
          return { data: await roleUsers.deleteUser(organizerId, payload) };
        } catch (error) {
          return asApiError(error);
        }
      },
      async onQueryStarted({ organizerId, payload }, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return;
        }
        const pages = cachedPages(getState() as RestApiRootState, organizerId);
        const holding = pages.filter(({ users }) => users.some((user) => user.id === payload.id));
        holding.forEach(({ args }) => {
          dispatch(roleUsersApi.util.updateQueryData('getRoleUsers', args, (draft) => draft.filter((user) => user.id !== payload.id)));
        });
        if (holding.length > 0) invalidateTail(dispatch, pages, Math.min(...holding.map(({ args }) => args.offset)));
      },
    }),
  }),
  overrideExisting: false,
});

export const { useGetRoleUsersQuery, useCreateRoleUserMutation, useUpdateRoleUserMutation, useDeleteRoleUserMutation } = roleUsersApi;
